import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Repeat,
  Plus,
  Search,
  Loader2,
  DollarSign,
  CalendarClock,
  AlertTriangle,
  Hash,
} from 'lucide-react';

interface Subscription {
  id: string;
  name: string;
  provider: string | null;
  description: string | null;
  businessProfileId: string | null;
  businessProfileName: string | null;
  cost: number;
  billingCycle: string;
  renewalDate: string | null;
  startDate: string;
  endDate: string | null;
  status: string;
  category: string | null;
  url: string | null;
  isTaxDeductible: boolean;
  taxCategory: string | null;
  notes: string | null;
  chargeAccountId: string | null;
  chargeAccountName: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}

interface ChargeAccountOption {
  id: string;
  name: string;
  issuer: string | null;
  last4: string | null;
  status: string;
}

interface SubscriptionOverviewData {
  totalMonthlyCost: number;
  totalAnnualCost: number;
  totalQuarterlyCost: number;
  totalSemiAnnualCost: number;
  activeCount: number;
  expiringCount: number;
  totalCount: number;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  active: 'success',
  cancelled: 'secondary',
  paused: 'warning',
  trial: 'info',
  expiring: 'destructive',
};


export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: chargeAccountsData } = useQuery({
    queryKey: ['chargeAccounts', { forAllocator: true }],
    queryFn: () => api.getChargeAccounts({ status: 'active', limit: 100 }),
  });

  const { data: overviewData } = useQuery({
    queryKey: ['subscriptionOverview', { businessProfileId: profileFilter }],
    queryFn: () => api.getSubscriptionOverview({
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', { page, search, status: statusFilter, businessProfileId: profileFilter }],
    queryFn: () => api.getSubscriptions({
      page,
      limit: 100,
      search: search || undefined,
      status: statusFilter || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const chargeAccounts = (chargeAccountsData?.data || []) as ChargeAccountOption[];
  const overview = overviewData?.data as SubscriptionOverviewData | undefined;
  const allSubs = (data?.data || []) as Subscription[];

  const monthlySubs = allSubs.filter(s => s.billingCycle === 'monthly');
  const annualSubs = allSubs.filter(s => s.billingCycle === 'annual');
  const otherSubs = allSubs.filter(s => s.billingCycle !== 'monthly' && s.billingCycle !== 'annual');

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      setDialogOpen(false);
      setEditingSub(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
    },
  });

  function openCreate() {
    setEditingSub(null);
    setDialogOpen(true);
  }

  function openEdit(sub: Subscription) {
    setEditingSub(sub);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      name: formData.get('name'),
      provider: formData.get('provider') || null,
      description: formData.get('description') || null,
      businessProfileId: formData.get('businessProfileId') || null,
      cost: parseFloat(formData.get('cost') as string),
      billingCycle: formData.get('billingCycle') || 'monthly',
      renewalDate: formData.get('renewalDate') || null,
      startDate: formData.get('startDate') || null,
      endDate: formData.get('endDate') || null,
      status: formData.get('status') || 'active',
      category: formData.get('category') || null,
      url: formData.get('url') || null,
      isTaxDeductible: formData.get('isTaxDeductible') === 'on',
      // Subscriptions classify under "Subscriptions" deduction class via the
      // auto-linked biz_expenses row. The legacy per-subscription tax_category
      // field is no longer set; tax form rendering reads from the linked expense.
      taxCategory: null,
      chargeAccountId: formData.get('chargeAccountId') || null,
      notes: formData.get('notes') || null,
    };

    if (editingSub) {
      updateMutation.mutate({ id: editingSub.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function renderSubTable(subs: Subscription[], title: string, periodLabel: string) {
    if (subs.length === 0) return null;
    const total = subs.reduce((sum, s) => sum + s.cost, 0);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className="text-sm text-muted-foreground">
            {subs.length} subscription{subs.length !== 1 ? 's' : ''} &middot; {formatCurrency(total)}{periodLabel}
          </span>
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Name</th>
                  <th className="text-left p-3 text-sm font-medium">Provider</th>
                  <th className="text-left p-3 text-sm font-medium">Business Profile</th>
                  <th className="text-right p-3 text-sm font-medium">Cost</th>
                  <th className="text-left p-3 text-sm font-medium">Paid with</th>
                  <th className="text-left p-3 text-sm font-medium">Renewal</th>
                  <th className="text-left p-3 text-sm font-medium">Status</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((sub) => (
                  <tr key={sub.id} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm font-medium">
                      {sub.name}
                      {sub.category && <span className="ml-2 text-xs text-muted-foreground">({sub.category})</span>}
                    </td>
                    <td className="p-3 text-sm">{sub.provider || '—'}</td>
                    <td className="p-3 text-sm">{sub.businessProfileName || '—'}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(sub.cost)}</td>
                    <td className="p-3 text-sm">
                      {sub.chargeAccountName ? (
                        <Badge variant="secondary">{sub.chargeAccountName}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-sm">{sub.renewalDate ? formatDate(sub.renewalDate) : '—'}</td>
                    <td className="p-3">
                      <Badge variant={statusVariant[sub.status] || 'secondary'}>{sub.status}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(sub)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { if (confirm('Delete this subscription?')) deleteMutation.mutate(sub.id); }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Subscription
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Cost</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalMonthlyCost || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><CalendarClock className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Annual Cost</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalAnnualCost || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><Hash className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-xl font-bold">{overview?.activeCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-destructive/10"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-xl font-bold">{overview?.expiringCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search subscriptions..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="expiring">Expiring</SelectItem>
          </SelectContent>
        </Select>
        <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All profiles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Subscription Tables */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : allSubs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Repeat className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No subscriptions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {renderSubTable(monthlySubs, 'Monthly Subscriptions', '/mo')}
          {renderSubTable(annualSubs, 'Annual Subscriptions', '/yr')}
          {renderSubTable(otherSubs, 'Other Subscriptions', '/period')}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingSub?.id || 'new'} className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSub ? 'Edit Subscription' : 'New Subscription'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input name="name" id="name" required defaultValue={editingSub?.name || ''} />
              </div>
              <div>
                <Label htmlFor="provider">Provider</Label>
                <Input name="provider" id="provider" placeholder="e.g., Microsoft" defaultValue={editingSub?.provider || ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input name="description" id="description" defaultValue={editingSub?.description || ''} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cost">Cost *</Label>
                <Input type="number" name="cost" id="cost" step="0.01" min="0.01" required defaultValue={editingSub?.cost || ''} />
              </div>
              <div>
                <Label htmlFor="billingCycle">Billing Cycle</Label>
                <select
                  name="billingCycle"
                  id="billingCycle"
                  defaultValue={editingSub?.billingCycle || 'monthly'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  name="status"
                  id="status"
                  defaultValue={editingSub?.status || 'active'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="paused">Paused</option>
                  <option value="trial">Trial</option>
                  <option value="expiring">Expiring</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="businessProfileId">Business Profile</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  defaultValue={editingSub?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None (General)</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input name="category" id="category" placeholder="e.g., Cloud, Design" defaultValue={editingSub?.category || ''} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  type="date"
                  name="startDate"
                  id="startDate"
                  defaultValue={editingSub?.startDate ? editingSub.startDate.split('T')[0] : new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  type="date"
                  name="endDate"
                  id="endDate"
                  defaultValue={editingSub?.endDate ? editingSub.endDate.split('T')[0] : ''}
                />
              </div>
              <div>
                <Label htmlFor="renewalDate">Renewal Date</Label>
                <Input
                  type="date"
                  name="renewalDate"
                  id="renewalDate"
                  defaultValue={editingSub?.renewalDate ? editingSub.renewalDate.split('T')[0] : ''}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="chargeAccountId">Paid with (Credit Card)</Label>
              <select
                name="chargeAccountId"
                id="chargeAccountId"
                defaultValue={editingSub?.chargeAccountId || ''}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Not on a credit card</option>
                {chargeAccounts.map(ca => (
                  <option key={ca.id} value={ca.id}>
                    {ca.name}{ca.last4 ? ` ****${ca.last4}` : ''}
                  </option>
                ))}
              </select>
              {chargeAccounts.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No active charge accounts. Add one on the Charge Accounts page to allocate bills.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="url">URL</Label>
              <Input name="url" id="url" placeholder="https://..." defaultValue={editingSub?.url || ''} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isTaxDeductible"
                  id="isTaxDeductible"
                  defaultChecked={editingSub?.isTaxDeductible || false}
                  className="h-4 w-4"
                />
                <Label htmlFor="isTaxDeductible">Tax Deductible</Label>
              </div>
              <p className="flex-1 text-xs text-muted-foreground">
                Tax form line: <span className="font-medium text-foreground">Subscriptions</span>
                <span className="ml-1">(Schedule C Line 18 — Office Expense / Form 1120 Other Deductions)</span>
              </p>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingSub?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingSub ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
